function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { Container } from "../Container/index.js";
import { Typography } from "../Typography/index.js";
import { cn } from "../../common/helpers.js";
const rootClasses = cva([
    'relative',
    'pt-8',
    'md:pt-12'
], {
    variants: {
        overlap: {
            true: [
                '-mb-12 lg:-mb-15 pb-20 lg:pb-[108px]'
            ],
            false: [
                'pb-8 md:pb-12'
            ]
        }
    }
});
const innerContainerClasses = cva([
    'max-w-[860px]'
], {
    variants: {
        alignment: {
            left: [],
            center: [
                'mx-auto'
            ]
        }
    }
});
/**
 * Title Background Block - [API](https://scl.sharp.com/?path=/docs/components-titlebackgroundblock) | [DSM](https://sharphealthcare.notion.site/Title-background-55dae31117a44b8197b008f4b7fee0d7)
 *
 * @example
 * <TitleBackgroundBlock
 *  heading="Heading text"
 *  description="Description text"
 *  image={{ src: 'floral.webp' }}
 * />
 *
 */ const TitleBackgroundBlock = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { heading, description, alignment = 'left', image = {}, overlap = false, className, slots = {}, slotProps = {}, as = 'div' } = _param, rest = _object_without_properties(_param, [
        "heading",
        "description",
        "alignment",
        "image",
        "overlap",
        "className",
        "slots",
        "slotProps",
        "as"
    ]);
    var _slotProps_description;
    const Root = as !== null && as !== void 0 ? as : 'div';
    var _slots_image;
    const Image = (_slots_image = slots.image) !== null && _slots_image !== void 0 ? _slots_image : 'img';
    const { className: imagePropClassName } = image, imagePropProps = _object_without_properties(image, [
        "className"
    ]);
    var _slotProps_image;
    const _ref = (_slotProps_image = slotProps.image) !== null && _slotProps_image !== void 0 ? _slotProps_image : {}, { className: imageSlotPropsClassName } = _ref, imageSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    const imageProps = slots.image ? _object_spread({}, imageSlotProps) : _object_spread({}, imagePropProps, imageSlotProps);
    var _slots_heading;
    const Heading = (_slots_heading = slots.heading) !== null && _slots_heading !== void 0 ? _slots_heading : Typography;
    var _slotProps_heading;
    const _ref1 = (_slotProps_heading = slotProps.heading) !== null && _slotProps_heading !== void 0 ? _slotProps_heading : {}, { className: headingSlotPropsClassName } = _ref1, headingSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    // If heading slot provided then do not spread Typography specific props else combine
    const headingProps = slots.heading ? headingSlotProps : _object_spread({
        variant: 'h1',
        align: alignment
    }, headingSlotProps);
    var _slots_description;
    const Description = (_slots_description = slots.description) !== null && _slots_description !== void 0 ? _slots_description : Typography;
    var _slotProps_description1;
    const _ref2 = (_slotProps_description1 = slotProps.description) !== null && _slotProps_description1 !== void 0 ? _slotProps_description1 : {}, { className: descriptionSlotPropsClassName } = _ref2, descriptionSlotProps = _object_without_properties(_ref2, [
        "className"
    ]);
    // If description slot provided then do not spread Typography specific props else combine
    const descriptionProps = slots.description ? descriptionSlotProps : _object_spread({
        variant: 'body',
        as: 'div',
        align: alignment
    }, descriptionSlotProps);
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({}, rest), {
        ref: ref,
        className: cn(rootClasses({
            overlap,
            className
        })),
        children: [
            /*#__PURE__*/ _jsx(Image, _object_spread({
                className: cn(imagePropClassName, imageSlotPropsClassName, 'absolute inset-0 w-full h-full -z-10 pointer-events-none object-cover object-center')
            }, imageProps)),
            /*#__PURE__*/ _jsx(Container, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: innerContainerClasses({
                        alignment
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(Heading, _object_spread_props(_object_spread({
                            className: cn('line-clamp-3 md:line-clamp-2', headingSlotPropsClassName)
                        }, headingProps), {
                            children: heading
                        })),
                        (description !== null && description !== void 0 ? description : (_slotProps_description = slotProps.description) === null || _slotProps_description === void 0 ? void 0 : _slotProps_description.children) && /*#__PURE__*/ _jsx(Description, _object_spread_props(_object_spread({
                            className: cn('scl-rich-text mt-4', descriptionSlotPropsClassName)
                        }, descriptionProps), {
                            children: description
                        }))
                    ]
                })
            })
        ]
    }));
});
export { TitleBackgroundBlock };
